import { Link } from "react-router-dom";
import { BookHeart, ArrowLeft, AlertTriangle } from "lucide-react";

const Section = ({ n, title, children }) => (
  <section className="mt-8" data-testid={`terms-section-${n}`}>
    <h2 className="font-serif text-2xl text-[#2C2A29]">{n}. {title}</h2>
    <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-[#3d3a38]">{children}</div>
  </section>
);

const Termini = () => (
  <div className="min-h-screen bg-[#FBF8F3]">
    <header className="border-b border-border">
      <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-serif text-lg" data-testid="terms-logo"><BookHeart className="w-5 h-5 text-primary" /> Eterna</Link>
        <Link to="/" className="text-sm text-muted-foreground hover:text-[#2C2A29] inline-flex items-center gap-1.5" data-testid="terms-back"><ArrowLeft className="w-4 h-4" /> Torna alla home</Link>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-6 py-10 fade-in">
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2C2A29]">Termini di Servizio</h1>
      <p className="text-muted-foreground mt-3">Ultimo aggiornamento: giugno 2026</p>

      <div className="mt-6 rounded-2xl border border-[#E7C9A9] bg-[#FBF0E4] p-4 flex gap-3 text-sm text-[#7a5320]" data-testid="terms-draft-notice">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        <p><strong>Bozza da validare.</strong> Questo testo è una bozza fornita a scopo di partenza e <strong>deve essere revisionato e approvato da un legale</strong> prima della pubblicazione ufficiale. Non costituisce consulenza legale.</p>
      </div>

      <Section n="1" title="Oggetto del servizio">
        <p><strong>Eterna</strong> è una piattaforma che ti permette di custodire ricordi digitali (testi, foto, video, voci, documenti e testamento) e di programmarne il rilascio a una data, a un evento o dopo la tua scomparsa, a favore delle persone che indichi.</p>
        <p>Il fondatore e responsabile del servizio è <strong>Massimiliano Costantini</strong>. Contatto: <a className="text-primary hover:underline" href="mailto:info@eterna.page">info@eterna.page</a>.</p>
      </Section>

      <Section n="2" title="Account e requisiti">
        <p>Per utilizzare Eterna devi avere almeno 18 anni e fornire informazioni veritiere. Sei responsabile della riservatezza delle tue credenziali e di ogni attività svolta con il tuo account. Durante la fase di prova l'accesso può essere riservato a chi dispone di un codice di invito.</p>
      </Section>

      <Section n="3" title="I tuoi contenuti">
        <p>I contenuti che carichi restano <strong>di tua proprietà</strong>. Ci concedi esclusivamente la licenza tecnica necessaria a conservarli, elaborarli e recapitarli ai destinatari secondo le tue istruzioni. Sei l'unico responsabile della liceità dei contenuti e del diritto di caricarli e condividerli.</p>
      </Section>

      <Section n="4" title="Rilascio dei contenuti e ruolo dei tutori">
        <p>Il rilascio programmato (a data, a evento o post-mortem) dipende dal corretto funzionamento del servizio, dalle azioni dei tutori da te nominati e, per il rilascio post-mortem, dalla comunicazione della scomparsa ed eventuale verifica del certificato di decesso. Pur adottando la massima cura, non possiamo garantire tempistiche istantanee né l'esito di eventi dipendenti da terzi.</p>
      </Section>

      <Section n="5" title="Piani, pagamenti e rinnovi">
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Piano gratuito:</strong> 300 MB di spazio, senza costi.</li>
          <li><strong>Piani a canone:</strong> abbonamento con addebito ricorrente (mensile o annuale) fino a disdetta.</li>
          <li><strong>Piani una tantum:</strong> pagamento unico per la durata indicata (a tempo determinato o "a vita").</li>
        </ul>
        <p>I pagamenti sono gestiti da Stripe. In caso di <strong>disdetta o mancato rinnovo</strong> di un piano a canone, lo spazio torna al limite gratuito di 300 MB: i contenuti eccedenti non verranno cancellati ma potrebbero non essere più visibili finché non riattivi un piano adeguato.</p>
        <p className="text-muted-foreground text-sm">[Da definire col legale: diritto di recesso, rimborsi, prezzi IVA inclusa/esclusa, durata "a vita".]</p>
      </Section>

      <Section n="6" title="Uso corretto">
        <p>Ti impegni a non usare Eterna per contenuti illeciti, offensivi, che violino diritti altrui o le leggi vigenti. Possiamo sospendere o chiudere account che violino questi Termini.</p>
      </Section>

      <Section n="7" title="Disponibilità e conservazione">
        <p>Ci impegniamo a mantenere il servizio disponibile e i dati al sicuro, ma non garantiamo un funzionamento ininterrotto e privo di errori. Ti consigliamo di conservare copie dei contenuti più importanti.</p>
      </Section>

      <Section n="8" title="Limitazione di responsabilità">
        <p>Nei limiti consentiti dalla legge, Eterna non risponde di danni indiretti o della perdita di contenuti dovuta a cause non imputabili al Titolare. Nulla in questi Termini limita i diritti inderogabili riconosciuti ai consumatori.</p>
        <p className="text-muted-foreground text-sm">[Da rivedere col legale in base alla normativa applicabile.]</p>
      </Section>

      <Section n="9" title="Modifiche ai Termini">
        <p>Potremo aggiornare questi Termini. Le modifiche rilevanti ti saranno comunicate tramite il servizio o via email; l'uso continuato dopo la comunicazione costituisce accettazione.</p>
      </Section>

      <Section n="10" title="Legge applicabile e foro">
        <p>Questi Termini sono regolati dalla legge italiana.</p>
        <p className="text-muted-foreground text-sm">[Da definire col legale: foro competente ed eventuali clausole per i consumatori.]</p>
      </Section>

      <p className="mt-10 text-sm text-muted-foreground">Vedi anche la nostra <Link to="/privacy" className="text-primary hover:underline" data-testid="terms-to-privacy">Informativa sulla Privacy</Link>.</p>
    </main>

    <footer className="border-t border-border">
      <div className="max-w-3xl mx-auto px-6 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-3">
        <span className="flex items-center gap-2"><BookHeart className="w-4 h-4 text-primary" /> Eterna</span>
        <div className="flex items-center gap-4 flex-wrap justify-center">
          <Link to="/privacy" className="hover:text-[#2C2A29] transition-colors" data-testid="terms-footer-privacy">Privacy</Link>
          <a href="mailto:info@eterna.page" className="hover:text-[#2C2A29] transition-colors">info@eterna.page</a>
          <span>© 2026 Eterna</span>
        </div>
      </div>
    </footer>
  </div>
);

export default Termini;
